import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';
import type {
  CalculatedLeaseRenewalOfferDto,
  CreateLeaseRenewalOfferAutomaticSettingDto,
  CreateLeaseRenewalOfferDto,
  LeaseRenewalOfferAutomaticSettingDto,
  LeaseRenewalOfferAutomaticSettingGroupsDto,
  LeaseRenewalOfferDto,
  LeaseRenewalOfferPaginationDto,
  LeaseRenewalOfferSummariesDto,
  LeaseRenewalStatus,
} from '~/swagger/portal/Api';

const LEASES_URL = '/api/leases';

export type LeasesRenewalsFilter = {
  page?: number;
  pageSize?: number;
  statuses?: `${LeaseRenewalStatus}`;
};

export class LeasesRenewalsRepository extends BaseRestRepository {
  public async getRenewalOffers(leaseId: string, params?: LeasesRenewalsFilter): Promise<LeaseRenewalOfferPaginationDto> {
    const url = urlWithQuery(`${LEASES_URL}/${leaseId}/renewals`, params);
    const response = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    return this.getJsonResponse(response);
  }
  public async createLeaseRenewalOffer(leaseId: string, model: CreateLeaseRenewalOfferDto): Promise<LeaseRenewalOfferDto> {
    const url = `${LEASES_URL}/${leaseId}/renewals`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(model),
    });
    return this.getJsonResponse(response);
  }

  public async getRenewalOffersSummaries(leaseId: string): Promise<LeaseRenewalOfferSummariesDto> {
    const url = `${LEASES_URL}/${leaseId}/renewals/summaries`;
    const response = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    return this.getJsonResponse(response);
  }
  public async sendSigning(leaseId: string, offerId: string): Promise<void> {
    const url = `${LEASES_URL}/${leaseId}/renewals/${offerId}/send-signing`;
    await this.fetchWithAuth(url, {
      method: 'POST',
    });
  }
  public async applyOffer(leaseId: string, offerId: string): Promise<LeaseRenewalOfferDto> {
    const url = `${LEASES_URL}/${leaseId}/renewals/${offerId}/apply`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
    });
    return this.getJsonResponse(response);
  }
  public async createAndApplyLeaseRenewalOffer(leaseId: string, model: CreateLeaseRenewalOfferDto): Promise<LeaseRenewalOfferDto> {
    const url = `${LEASES_URL}/${leaseId}/renewals/apply`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(model),
    });
    return this.getJsonResponse(response);
  }

  public async deleteOffer(leaseId: string, offerId: string): Promise<void> {
    const url = `${LEASES_URL}/${leaseId}/renewals/${offerId}`;
    await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
  }

  public async getCalculatedLeaseRenewalOffer(leaseId: string): Promise<CalculatedLeaseRenewalOfferDto> {
    const url = `${LEASES_URL}/${leaseId}/renewals/calculate`;
    const response = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    return this.getJsonResponse(response);
  }

  public async getAutomaticSettings(): Promise<LeaseRenewalOfferAutomaticSettingGroupsDto> {
    const url = `${LEASES_URL}/renewals/automatic`;
    const response = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    return this.getJsonResponse(response);
  }

  public async createAutomaticSetting(data: CreateLeaseRenewalOfferAutomaticSettingDto): Promise<LeaseRenewalOfferAutomaticSettingDto> {
    const url = `${LEASES_URL}/renewals/automatic`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return this.getJsonResponse(response);
  }

  public async deleteAutomaticSetting(id: string): Promise<void> {
    const url = `${LEASES_URL}/renewals/automatic/${id}`;
    await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
  }
}
